import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from 'src/app/private/services/api.service';
import { environment } from 'src/environments/environment';
import { buildWebSocketUrl } from 'src/app/private/utils/web-live-chat-ws.util';

function unwrapData<T>(body: any): T {
  if (body && typeof body === 'object' && 'data' in body) {
    return body.data as T;
  }
  return body as T;
}

export interface WebChatStartResult {
  session_id: string;
  ws_url: string;
  ws_token: string;
  websocketUrl: string;
  status: string;
}

@Injectable({ providedIn: 'root' })
export class WebLiveChatService {
  constructor(private api: ApiService) {}

  createWebChatLead(payload: {
    embed_token: string;
    customer_name?: string;
    contact_name?: string;
    customer_email?: string;
    contact_email?: string;
    customer_phone?: string;
    contact_phone?: string;
    country_code?: string;
  }): Observable<any> {
    const body = {
      embed_token: payload.embed_token,
      customer_name: payload.customer_name ?? payload.contact_name,
      customer_email: payload.customer_email ?? payload.contact_email,
      customer_phone: payload.customer_phone ?? payload.contact_phone,
      country_code: payload.country_code,
    };
    return this.api
      .post<any>(environment.createWebChatLead, body)
      .pipe(map((res) => ({ ...unwrapData(res), ...res })));
  }

  startWebChat(payload: {
    embed_token: string;
    visitor_id: string;
    customer_name: string;
    subject: string;
    lead_id?: number;
    customer_id?: string;
    customer_email?: string;
    customer_phone?: string;
  }): Observable<WebChatStartResult> {
    return this.api.post<any>(environment.startWebChat, payload).pipe(
      map((res) => {
        const data = unwrapData<any>(res);
        const wsUrl = String(data?.ws_url || environment.webChatWsUrl || '').trim();
        const wsToken = String(data?.ws_token || '').trim();
        return {
          session_id: data?.session_id,
          ws_url: wsUrl,
          ws_token: wsToken,
          websocketUrl: buildWebSocketUrl(wsUrl, wsToken),
          status: data?.status || 'WAITING',
        };
      })
    );
  }

  sendWebChatMessage(payload: {
    session_id: string;
    content: string;
    embed_token?: string;
    visitor_id?: string;
    client_id?: string | number;
    agent_id?: string | number;
    agent_name?: string;
    attachments?: Array<{ key: string; name: string; mime?: string; size?: number }>;
  }): Observable<any> {
    return this.api
      .post<any>(environment.sendWebChatMessage, payload)
      .pipe(map((res) => unwrapData(res)));
  }

  getWebChatMessages(payload: {
    session_id: string;
    embed_token?: string;
    client_id?: string | number;
    limit?: number;
    after_ulid?: string;
    nextToken?: string;
  }): Observable<{ items: any[]; nextToken?: string | null; hasMore?: boolean }> {
    return this.api.post<any>(environment.getWebChatMessages, payload).pipe(
      map((res) => {
        const data = unwrapData<any>(res);
        return {
          items: data?.items || [],
          nextToken: data?.nextToken ?? null,
          hasMore: Boolean(data?.hasMore),
        };
      })
    );
  }

  endWebChat(payload: {
    session_id: string;
    embed_token?: string;
    visitor_id?: string;
    client_id?: string | number;
    agent_id?: string | number;
    agent_name?: string;
  }): Observable<any> {
    return this.api
      .post<any>(environment.endWebChat, payload)
      .pipe(map((res) => unwrapData(res)));
  }

  getWebChatQueue(payload: {
    client_id: string | number;
    status?: 'WAITING' | 'ACTIVE';
    limit?: number;
  }): Observable<{ waiting: any[]; active: any[] }> {
    return this.api.post<any>(environment.getWebChatQueue, payload).pipe(
      map((res) => {
        const data = unwrapData<any>(res);
        return {
          waiting: data?.waiting || [],
          active: data?.active || [],
        };
      })
    );
  }

  takeoverWebChat(payload: {
    session_id: string;
    client_id: string | number;
    agent_id: string | number;
    agent_name?: string;
  }): Observable<any> {
    return this.api
      .post<any>(environment.takeoverWebChat, payload)
      .pipe(map((res) => unwrapData(res)));
  }

  resolveWebChat(payload: {
    session_id: string;
    client_id: string | number;
    agent_id?: string | number;
    agent_name?: string;
  }): Observable<any> {
    return this.api
      .post<any>(environment.resolveWebChat, payload)
      .pipe(map((res) => unwrapData(res)));
  }

  uploadWebChatAttachment(payload: {
    session_id: string;
    file_name: string;
    mime_type: string;
    file_size: number;
    embed_token?: string;
    client_id?: string | number;
  }): Observable<{ upload_url: string; object_key: string; expires_in: number }> {
    return this.api.post<any>(environment.uploadWebChatAttachment, payload).pipe(
      map((res) => unwrapData<any>(res))
    );
  }

  getWebChatAttachmentUrl(payload: {
    session_id: string;
    object_key: string;
    embed_token?: string;
    client_id?: string | number;
  }): Observable<{ download_url: string }> {
    return this.api.post<any>(environment.getWebChatAttachmentUrl, payload).pipe(
      map((res) => unwrapData<any>(res))
    );
  }

  async putFileToPresignedUrl(uploadUrl: string, file: File, mimeType: string): Promise<void> {
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': mimeType || file.type || 'application/octet-stream' },
      body: file,
    });
    if (!res.ok) {
      throw new Error('File upload failed');
    }
  }

  uploadAttachmentFile(
    meta: {
      session_id: string;
      embed_token?: string;
      client_id?: string | number;
    },
    file: File
  ): Observable<{ object_key: string; name: string; mime: string; size: number }> {
    return new Observable((observer) => {
      this.uploadWebChatAttachment({
        ...meta,
        file_name: file.name,
        mime_type: file.type || 'application/octet-stream',
        file_size: file.size,
      }).subscribe({
        next: async (presign) => {
          try {
            await this.putFileToPresignedUrl(
              presign.upload_url,
              file,
              file.type || 'application/octet-stream'
            );
            observer.next({
              object_key: presign.object_key,
              name: file.name,
              mime: file.type || 'application/octet-stream',
              size: file.size,
            });
            observer.complete();
          } catch (err) {
            observer.error(err);
          }
        },
        error: (err) => observer.error(err),
      });
    });
  }
}
